import React, { useEffect, useRef, useState, useMemo } from "react";
import Chart from "chart.js/auto";

const StandardsPerformanceChart = ({
  loading = false,
  error = null,
  data = [],
}) => {
  const standardsRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [selectedFilter, setSelectedFilter] = useState("all");

  // Filter data based on selection
  const filteredData = useMemo(() => {
    if (!data || !data.length) return [];

    const sortedData = [...data].sort(
      (a, b) => b.completionRate - a.completionRate
    );

    switch (selectedFilter) {
      case "top5":
        return sortedData.slice(0, 5);
      case "bottom5":
        return sortedData.slice(-5).reverse();
      default:
        return data;
    }
  }, [data, selectedFilter]);

  useEffect(() => {
    if (!standardsRef.current || !filteredData || !filteredData.length) return;

    // Destroy existing chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    if (loading || !filteredData.length) {
      return;
    }

    // Prepare chart data
    const labels = filteredData.map((item) => item.standardName);
    const completionRates = filteredData.map((item) => item.completionRate);

    chartInstance.current = new Chart(standardsRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Completion Rate (%)",
            data: completionRates,
            backgroundColor: "rgba(79,70,229,0.7)",
            borderColor: "rgba(79,70,229,1)",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            title: {
              display: true,
              text: "Completion Rate (%)",
            },
          },
          x: {
            title: {
              display: true,
              text: "Standards",
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "top" as const,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const dataIndex = context.dataIndex;
                const item = filteredData[dataIndex];
                return [
                  `Completion Rate: ${item.completionRate}%`,
                  `Completed: ${item.completed}/${item.total}`,
                ];
              },
            },
          },
        },
      },
    });

    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [filteredData, loading]);

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  const getButtonClass = (filter) => {
    return `text-xs px-2 py-1 rounded ${
      selectedFilter === filter
        ? "bg-indigo-100 text-indigo-700"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`;
  };

  if (loading) {
    return (
      <div className="bg-white p-5 border border-gray-200 rounded shadow-sm">
        <div className="flex justify-between items-center mb-4 md:flex-row flex-col">
          <h3 className="text-lg font-medium text-gray-900">
            Standards Performance
          </h3>
          <div className="flex items-center space-x-2">
            <button
              className={getButtonClass("all")}
              onClick={() => handleFilterChange("all")}
            >
              All Standards
            </button>
            <button
              className={getButtonClass("top5")}
              onClick={() => handleFilterChange("top5")}
            >
              Top 5
            </button>
            <button
              className={getButtonClass("bottom5")}
              onClick={() => handleFilterChange("bottom5")}
            >
              Bottom 5
            </button>
          </div>
        </div>
        <div className="h-72 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">
              Loading standards performance...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.length) {
    return (
      <div className="bg-white p-5 border border-gray-200 rounded shadow-sm">
        <div className="flex justify-between items-center mb-4 md:flex-row flex-col">
          <h3 className="text-lg font-medium text-gray-900">
            Standards Performance
          </h3>
          <div className="flex items-center space-x-2">
            <button
              className={getButtonClass("all")}
              onClick={() => handleFilterChange("all")}
            >
              All Standards
            </button>
            <button
              className={getButtonClass("top5")}
              onClick={() => handleFilterChange("top5")}
            >
              Top 5
            </button>
            <button
              className={getButtonClass("bottom5")}
              onClick={() => handleFilterChange("bottom5")}
            >
              Bottom 5
            </button>
          </div>
        </div>
        <div className="h-72 flex items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 border border-gray-200 rounded shadow-sm">
      <div className="flex justify-between items-center mb-4 md:flex-row flex-col">
        <h3 className="text-lg font-medium text-gray-900">
          Standards Performance
        </h3>
        <div className="flex items-center space-x-2">
          <button
            className={getButtonClass("all")}
            onClick={() => handleFilterChange("all")}
          >
            All Standards
          </button>
          <button
            className={getButtonClass("top5")}
            onClick={() => handleFilterChange("top5")}
          >
            Top 5
          </button>
          <button
            className={getButtonClass("bottom5")}
            onClick={() => handleFilterChange("bottom5")}
          >
            Bottom 5
          </button>
        </div>
      </div>
      <div className="h-72">
        <canvas ref={standardsRef}></canvas>
      </div>
    </div>
  );
};

export default StandardsPerformanceChart;
